import {
	selectPromosForWidget,
	type WidgetSelectionConfig,
} from '@supply-mover-2-0/core';
import type {
	PortalPromoDto,
	PortalSiteDisplaySettingsDto,
} from '@supply-mover-2-0/shared';
import {
	PromoBannerWidget,
	PromoCarouselWidget,
	PromoGridWidget,
	PromoOneUpWidget,
	PromoThreeUpWidget,
	PromoTwoUpWidget,
	useTranslation,
	type PromoClickTarget,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import { PAGE_CONTENT } from '../../lib/pageWidth';
import {
	getStorefrontDashboard,
	getStorefrontPromos,
	type PortalDashboardWidget,
} from '../../services/portalDashboardService';
import { PrimaryPaymentMethodCard } from '../PrimaryPaymentMethodCard';
import { RecentInvoicesCard } from '../RecentInvoicesCard';
import { RecentOrdersCard } from '../RecentOrdersCard';
import { RecentStatementsCard } from '../RecentStatementsCard';
import { RecentTransactionsCard } from '../RecentTransactionsCard';
import { AccountProfileWidget } from './widgets/AccountProfileWidget';
import { AccountSnapshotWidget } from './widgets/AccountSnapshotWidget';
import { CatalogWidget } from './widgets/CatalogWidget';
import { CategoryTilesWidget } from './widgets/CategoryTilesWidget';
import { GetHelpWidget } from './widgets/GetHelpWidget';
import { ItemsByCategoryWidget } from './widgets/ItemsByCategoryWidget';
import { LastOrderWidget } from './widgets/LastOrderWidget';
import { PopularItemsWidget } from './widgets/PopularItemsWidget';
import { buildPromoHref } from './widgets/promoNavigation';
import { PromoWithItemsWidget } from './widgets/PromoWithItemsWidget';
import { ReorderItemsWidget } from './widgets/ReorderItemsWidget';
import { SubmitIssueWidget } from './widgets/SubmitIssueWidget';

// Widget types that hard-require a logged-in customer to render anything
// useful (they return null without a token). Filtered out for anon visitors
// so their layout rows don't leave dead vertical gaps. Module-scope so the
// reference is stable across renders (no exhaustive-deps churn).
// `account-snapshot` deliberately stays OFF this list — for anon visitors it
// renders a sign-in CTA in its slot (a conversion surface, not a dead gap).
const AUTH_ONLY_WIDGET_TYPES = [
	'last-order',
	'reorder-items',
	'account-profile',
	'recent-orders',
	'recent-invoices',
	'recent-transactions',
	'recent-statements',
	'payment-methods',
	'submit-issue',
	'get-help',
];

// Account-portal-only storefronts (allowCatalogBrowsing off) have no catalog,
// so every browse / shopping / promo widget is meaningless — keep every
// widget that renders without one (PD-14691). Allow-list (not block-list) so
// any future shopping widget type is excluded by default rather than leaking
// into the account portal.
const ACCOUNT_PORTAL_WIDGET_TYPES = [
	'account-snapshot',
	'account-profile',
	'recent-orders',
	'recent-invoices',
	'recent-transactions',
	'recent-statements',
	'payment-methods',
	'last-order',
	'submit-issue',
	'get-help',
];

/**
 * Per-widget capability gate (PD-14691). Order widgets need the order-history
 * surface, each financial widget needs ITS surface (invoices / statements /
 * transactions are independently toggleable per portal since PD-14675), and
 * the saved-payment-method summary needs payments. The per-user hidePricing
 * flag (PD-14239) is covered for free: the server forces canViewInvoices,
 * canViewStatements, and canViewTransactions all off for those users, so no
 * financial widget can mount. Unknown/browse widget types pass through — the
 * catalog allow-list above and the dispatch's default case own those.
 */
function isWidgetAllowedForDisplay(
	type: string | null | undefined,
	display: PortalSiteDisplaySettingsDto,
): boolean {
	switch (type) {
		case 'recent-orders':
		case 'last-order':
			return display.canViewOrderHistory;
		case 'account-snapshot':
		case 'recent-invoices':
			return display.canViewInvoices;
		case 'recent-statements':
			return display.canViewStatements;
		case 'recent-transactions':
			return display.canViewTransactions;
		case 'payment-methods':
			return display.canPayInvoices;
		default:
			return true;
	}
}

/**
 * Storefront homepage dashboard. Resolves the design for the active
 * portal_site (admin-authored in the CRM Settings → Storefront →
 * Dashboard Designs page) and renders it via the SAME `WidgetGrid`
 * component the CRM dashboard uses (in libs/web-ui), with promo-
 * specific widget renderers wired through the `renderWidget` slot.
 *
 * The storefront uses the no-op data + filter adapters because each
 * promo widget self-fetches its candidates (one shared
 * `/api/portal/dashboard/promos` query at the top level, then
 * sliced by each widget's selection config). Storefront widgets
 * have no per-widget filter chrome.
 *
 * Returns null when no storefront design is assigned to the portal
 * (and no tenant-default exists). Caller falls back to the action-
 * tile DashboardPage in that case.
 */
export function StorefrontDashboard(): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();

	const dashboardQuery = useQuery({
		queryKey: ['portal', 'dashboard'],
		queryFn: () => getStorefrontDashboard(token),
		staleTime: 60_000,
	});

	const promosQuery = useQuery({
		queryKey: ['portal', 'dashboard', 'promos'],
		queryFn: () => getStorefrontPromos(token),
		staleTime: 30_000,
	});

	// Wired into every promo / category widget below: when an admin-uploaded
	// banner image fails to load (typically a stale presigned URL), each
	// widget calls this once to refetch the parent payload and get a
	// freshly-signed URL. Bounded to a single retry per src by ResilientImage.
	const refetchPromosForStaleImage = (): void => {
		void queryClient.invalidateQueries({
			queryKey: ['portal', 'dashboard', 'promos'],
		});
	};

	const dashboard = dashboardQuery.data?.data ?? null;
	const eligiblePromos = promosQuery.data?.data ?? [];

	// Widget types that hard-require a logged-in customer to render
	// anything useful. The widgets themselves return `null` when there's
	// no token, but their wrapper divs in the layout below still occupy
	// a CSS-grid row and contribute to the parent's `space-y-6` gap. On
	// mobile that produced ~48px of dead vertical space between the
	// rendered widgets above and below them. Filtering here strips
	// them from the layout entirely so the row doesn't exist when
	// they would have nothing to show.
	const isAnon = token == null;
	const canBrowseCatalog = display.canBrowseCatalog;

	// Group widgets by their row (position.y) so widgets the admin put
	// side-by-side (e.g. Account + Last Order both at y=2) share a
	// CSS-grid row, while everything else stacks. Sort within a row by
	// position.x so column order matches the design.
	//
	// Storefront layout intentionally diverges from the CRM
	// WidgetGrid (absolute-positioned, fixed-height cells): the
	// customer-facing surface needs CONTENT-driven heights and
	// auto-reflow when a widget self-hides (e.g. Reorder when the
	// customer has no qualifying purchase history). CSS grid + `null`
	// returns from widgets give us that for free; the absolute grid
	// would have left an empty cell-shaped hole instead.
	//
	// We pre-filter widgets that we KNOW will return null (auth-only
	// types when the visitor is anon). Widgets that conditionally
	// return null based on data (Last Order with no orders, Reorder
	// with no qualifying history) still fall through to the runtime
	// null path; those are rare enough that the residual gap is
	// acceptable.
	const widgetRows = useMemo<Array<Array<PortalDashboardWidget>>>(() => {
		if (dashboard == null) return [];
		const byRow = new Map<number, Array<PortalDashboardWidget>>();
		for (const w of dashboard.widgets) {
			if (isAnon && w.type != null && AUTH_ONLY_WIDGET_TYPES.includes(w.type)) {
				continue;
			}
			if (
				!canBrowseCatalog &&
				(w.type == null || !ACCOUNT_PORTAL_WIDGET_TYPES.includes(w.type))
			) {
				continue;
			}
			// Capability-gated widgets (order history off, statements off, …)
			// are stripped here — not just nulled in the dispatch — so their
			// wrapper divs never occupy a grid row (same dead-gap rationale as
			// the anon filter above).
			if (!isWidgetAllowedForDisplay(w.type, display)) {
				continue;
			}
			const y = w.position.y;
			const arr = byRow.get(y) ?? [];
			arr.push(w);
			byRow.set(y, arr);
		}
		return Array.from(byRow.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([, ws]) => ws.sort((a, b) => a.position.x - b.position.x));
	}, [dashboard, isAnon, canBrowseCatalog, display]);

	if (dashboardQuery.isLoading || promosQuery.isLoading) {
		return (
			<div className={`px-4 py-6 ${PAGE_CONTENT}`}>
				<div className="animate-pulse space-y-4">
					<div className="h-32 bg-brand-input rounded-xl" />
					<div className="h-48 bg-brand-input rounded-xl" />
				</div>
			</div>
		);
	}

	if (dashboard == null || widgetRows.length === 0) {
		// A dashboard whose widgets are ALL suppressed (e.g. a catalog-focused
		// config on an account-portal-only storefront) must not render a blank
		// Home (PD-14686). With the expanded account allow-list (PD-14691)
		// this is a rare misconfiguration state, so rather than maintaining a
		// bespoke quick-links page for it, send the visitor to the account
		// page — the one surface every authenticated portal user has.
		// Anonymous visitors on such portals only see the login prompt, so
		// this renders nothing for them (the welcome screen owns that state).
		if (isAnon) {
			return null;
		}
		return <Navigate to="/account" replace />;
	}

	return (
		<div
			className={`px-4 py-4 ${PAGE_CONTENT} space-y-6`}
			data-testid="storefront-dashboard"
			aria-label={t(dashboard.name)}
		>
			{widgetRows.map((row, rowIdx) => (
				<div
					key={rowIdx}
					className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6"
				>
					{row.map((w) => (
						<div
							key={w.id}
							// On md+ each widget spans its declared width
							// out of 4. Mobile collapses to single
							// column so widgets stack naturally and
							// each gets full width.
							className={
								MD_COL_SPAN[Math.min(Math.max(w.position.width, 1), 4)]
							}
						>
							<StorefrontWidgetDispatch
								widget={w}
								eligiblePromos={eligiblePromos}
								onLikelyStalePromoImage={refetchPromosForStaleImage}
							/>
						</div>
					))}
				</div>
			))}
		</div>
	);
}

// Tailwind needs literal class names for the JIT compiler to keep
// these in the final bundle. Mapping by width value rather than
// constructing the class name dynamically.
export const MD_COL_SPAN: Record<number, string> = {
	1: 'md:col-span-1',
	2: 'md:col-span-2',
	3: 'md:col-span-3',
	4: 'md:col-span-4',
};

/**
 * Maps `widget.type` → the corresponding promo widget component.
 * Each widget receives the full eligible-promo list and applies its
 * own selection config (auto rankBy + limit, or manual ID list).
 *
 * Widgets self-hide by returning `null` when they have no useful
 * content (Reorder with no qualifying history, Last Order with no
 * orders, Popular with no in-stock items, etc). The CSS-grid row
 * collapses naturally — no empty cells, no empty whitespace.
 */
export function StorefrontWidgetDispatch({
	widget,
	eligiblePromos,
	onLikelyStalePromoImage,
}: {
	widget: PortalDashboardWidget;
	eligiblePromos: Array<PortalPromoDto>;
	/** Forwarded to every promo widget. See `PromoTile.onLikelyStaleUrl`. */
	onLikelyStalePromoImage: () => void;
}): JSX.Element | null {
	const display = useDisplaySettings();
	const navigate = useNavigate();

	// The shared promo tiles resolve a click to an intent and leave the
	// navigation to their host, so that the same components can render
	// inertly inside the CRM storefront designer. This is the
	// storefront's half of that contract.
	const handlePromoSelect = useCallback(
		(target: PromoClickTarget): void => {
			if (target.kind === 'external-url') {
				window.open(target.url, '_blank', 'noopener,noreferrer');
				return;
			}
			const href = buildPromoHref(target);
			if (href != null) {
				navigate(href);
			}
		},
		[navigate],
	);

	// Capability gate (PD-14691, subsuming the PD-14239 hide-pricing gates):
	// a widget whose backing surface is disabled for this visitor never
	// mounts, so no balances/totals are ever fetched. The homepage strips
	// these from the layout too; this is the backstop for other callers
	// (account-page designer) and mid-session flag changes.
	if (!isWidgetAllowedForDisplay(widget.type, display)) {
		return null;
	}

	const settings = (widget.settings ?? {}) as Record<string, unknown>;
	const selectionConfig: WidgetSelectionConfig = (settings.selection as
		| WidgetSelectionConfig
		| undefined) ?? {
		mode: 'auto',
		auto: { rankBy: 'priority' },
	};
	const selected = selectPromosForWidget(eligiblePromos, selectionConfig);

	// Layout-style settings configured per-instance via the widget
	// settings drawer. Null/undefined means "use the widget's
	// default" — the renderer then falls back to its built-in
	// behaviour (5s carousel rotation; 4 grid columns).
	const carouselMs = settings.carouselAutoAdvanceMs as
		| number
		| null
		| undefined;
	const gridCols = settings.gridColumns as number | null | undefined;

	// Utility widgets self-fetch their own payload — they don't slice
	// the shared promo list. Each accepts an optional `title` (admin-
	// authored in the dashboard designer) and a `limit` for the
	// item-carousel widgets so the design tool can right-size the
	// content to its grid cell.
	const itemLimit = settings.itemLimit as number | null | undefined;
	// `density` is admin-set per widget instance via widgetConfig — `compact`
	// shrinks mobile sizes ~25–40% (shorter promo aspects, narrower item /
	// category cards) without touching desktop. Defaults to `standard`.
	const density =
		settings.density === 'compact' ? 'compact' : ('standard' as const);
	const promoId =
		typeof settings.promoId === 'number' ? settings.promoId : null;
	const category =
		typeof settings.category === 'string' ? settings.category : null;

	switch (widget.type) {
		case 'promo-banner':
			return (
				<PromoBannerWidget
					promos={selected}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-one-up':
			return (
				<PromoOneUpWidget
					promos={selected}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-two-up':
			return (
				<PromoTwoUpWidget
					promos={selected}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-three-up':
			return (
				<PromoThreeUpWidget
					promos={selected}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-carousel':
			return (
				<PromoCarouselWidget
					promos={selected}
					rotateMs={carouselMs ?? undefined}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-grid':
			return (
				<PromoGridWidget
					promos={selected}
					columns={gridCols ?? undefined}
					density={density}
					onSelect={handlePromoSelect}
					onLikelyStaleUrl={onLikelyStalePromoImage}
				/>
			);
		case 'promo-with-items':
			// No `onLikelyStaleUrl` here — the widget no longer renders
			// a PromoTile (the decorative strip band was dropped); the
			// inner ItemCarousel handles its own stale-image refresh
			// via `invalidateQueryKey`.
			return (
				<PromoWithItemsWidget
					title={widget.title}
					promoId={promoId}
					limit={itemLimit ?? undefined}
					density={density}
				/>
			);
		case 'items-by-category':
			return (
				<ItemsByCategoryWidget
					title={widget.title}
					category={category}
					limit={itemLimit ?? undefined}
					density={density}
				/>
			);
		case 'catalog':
			// The full catalog experience embedded on the homepage — place
			// promo/content widgets above or below it to compose a shop
			// page. One per design (URL-param state; see CatalogWidget).
			return <CatalogWidget title={widget.title} />;
		case 'account-snapshot':
			return <AccountSnapshotWidget title={widget.title} />;
		case 'account-profile':
			return <AccountProfileWidget />;
		case 'recent-orders':
			return <RecentOrdersCard title={widget.title} />;
		case 'recent-invoices':
			return <RecentInvoicesCard title={widget.title} />;
		case 'recent-transactions':
			return <RecentTransactionsCard title={widget.title} />;
		case 'recent-statements':
			return <RecentStatementsCard title={widget.title} />;
		case 'payment-methods':
			return <PrimaryPaymentMethodCard title={widget.title} />;
		case 'last-order':
			return <LastOrderWidget title={widget.title} />;
		case 'submit-issue':
			return <SubmitIssueWidget title={widget.title} />;
		case 'get-help':
			return <GetHelpWidget title={widget.title} />;
		case 'reorder-items':
			return (
				<ReorderItemsWidget
					title={widget.title}
					limit={itemLimit ?? undefined}
					density={density}
				/>
			);
		case 'popular-items':
			return (
				<PopularItemsWidget
					title={widget.title}
					limit={itemLimit ?? undefined}
					density={density}
				/>
			);
		case 'category-tiles':
			return (
				<CategoryTilesWidget
					title={widget.title}
					limit={itemLimit ?? undefined}
					density={density}
				/>
			);
		default:
			// A widget type this storefront build doesn't support (e.g. a
			// CRM-only type assigned via dashboard config) renders nothing
			// rather than an error placeholder visible to customers.
			console.debug(
				`StorefrontDashboard: skipping unsupported widget type "${widget.type}"`,
			);
			return null;
	}
}
